import { ActiveFilterChips } from "./components/filters/ActiveFilterChips";
import { CoordinateBar } from "./components/map/CoordinateBar";
import { DetailPanel } from "./components/detail/DetailPanel";
import { FiltersPanel } from "./components/filters/FiltersPanel";
import { GradientLegend } from "./components/layers/GradientLegend";
import { LayersPanel } from "./components/layers/LayersPanel";
import { Header } from "./components/layout/Header";
import { MapStates } from "./components/states/MapStates";
import { TimelineBar } from "./components/timeline/TimelineBar";
import { useUrlFilterSync } from "./hooks/useUrlFilterSync";
import { MapView } from "./map/MapView";
import { getBasemap } from "./map/basemaps";
import { useLayersStore } from "./store/layersStore";

/**
 * The single working screen. The map is the hero and fills all remaining
 * space; panels float over it. The overlay is pointer-transparent except on
 * the panels themselves, so the map stays fully interactive between them.
 */
export function App() {
  // Keep the applied filters mirrored in the URL so a view can be shared.
  useUrlFilterSync();
  const basemapId = useLayersStore((s) => s.basemapId);
  const imagery = getBasemap(basemapId).imagery === true;

  return (
    <div className="flex h-full flex-col" data-map-imagery={imagery ? "true" : "false"}>
      <main className="relative flex-1 overflow-hidden">
        <MapView />

        {/* Glass header floating over the map. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30">
          <div className="pointer-events-auto">
            <Header />
          </div>
        </div>

        {/* Left column: filters then layers, stacked so they never overlap each
            other. Height is capped to the space above the timeline (with scroll
            if a short screen can't fit both open) so it never covers it. */}
        <div className="pointer-events-auto absolute left-3 top-[4.75rem] z-20 flex max-h-[calc(100%-5.5rem)] flex-col items-start gap-2 overflow-y-auto pb-1">
          <FiltersPanel />
          <LayersPanel />
        </div>

        {/* Active-filter chips, centered just below the header. */}
        <div className="pointer-events-none absolute left-1/2 top-[4.75rem] z-10 -translate-x-1/2">
          <div className="pointer-events-auto">
            <ActiveFilterChips />
          </div>
        </div>

        {/* Legend, bottom-right. */}
        <div className="pointer-events-none absolute inset-0">
          <div className="pointer-events-auto absolute bottom-8 right-3">
            <GradientLegend />
          </div>
        </div>

        {/* Cursor readout, centred on the bottom edge — the gap between the
            scale bar on the left and the attribution on the right. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-1.5 z-10 flex justify-center">
          <div className="pointer-events-auto">
            <CoordinateBar />
          </div>
        </div>

        <MapStates />
        <DetailPanel />
      </main>
      <TimelineBar />
    </div>
  );
}
